import { expand } from 'uri-template-lite';
import {
  AUTOCOMPLETE_PAGE_SIZE,
  OptionsPage,
  OptionType,
  Page,
  PageRequestParams,
  Sort,
} from '../../common/queryClient';
import { apiClient } from '../../common/ApiClient';
import { createApiQueryHook } from '../../common/queryClient/createApiQueryHook';
import { DataSubjectUrl } from './DataSubjectUrl';
import { DataSubjectListDto, DataSubjectSortField } from '../types';
import { QueryFunctionContext } from '@tanstack/react-query';
import { useInfiniteSearchQuery } from '../../common/queryClient/useInfiniteSearchQuery';
import { formatCodeName } from '../../common/utils';
import { NamedEntity } from '../../common/types';

export interface DataSubjectFilters {
  search?: string;
  businessArea?: string[];
  economicSector?: number[];
  legalForm?: number[];
  isFictitious?: boolean;
  isPublic?: boolean;
  selectedSubject?: string;
}

export type DataSubjectFilterKey = keyof DataSubjectFilters;

type DataSubjectQueryParams = DataSubjectFilters &
  PageRequestParams<DataSubjectSortField>;

export interface DataSubjectOption extends OptionType {
  item: DataSubjectListDto;
}

export function addIdSort(sort?: Sort) {
  if (!sort) {
    return sort;
  }

  if (Array.isArray(sort)) {
    if (sort.length === 0) {
      return sort;
    }

    const last = sort.at(-1);
    const order = last?.endsWith('desc') ? 'desc' : 'asc';
    return [...sort, `id,${order}`];
  }

  const order = sort.endsWith('desc') ? 'desc' : 'asc';
  return [sort, `id,${order}`];
}

export const getDataSubjects = (params?: DataSubjectQueryParams) =>
  apiClient
    .get<Page<DataSubjectListDto>>(
      expand(DataSubjectUrl.DATASUBJECT, {
        ...params,
        sort: addIdSort(params?.sort),
      }),
    )
    .then((response) => response.data);

const createSearchDataSubjects = async ({
  meta,
  pageParam,
}: QueryFunctionContext<any, number>): Promise<OptionsPage> => {
  const page = pageParam ?? 0;
  const result = await getDataSubjects({
    search: (meta?.searchText ?? '') as string,
    page,
    size: AUTOCOMPLETE_PAGE_SIZE,
    selectedSubject: meta?.selectedSubject as string,
  });

  const content: DataSubjectOption[] = result.content.map((item) => {
    return {
      value: item.code,
      label: formatCodeName(item as NamedEntity),
      item: item,
    };
  });
  return {
    content,
    last: result.last,
    number: result.number,
  };
};

export function useInfiniteSearchDataSubjectsQuery(
  term: string,
  selectedSubject?: string,
) {
  return useInfiniteSearchQuery(
    ['search-datasubjects-autocomplete'],
    createSearchDataSubjects,
    term,
    { selectedSubject },
  );
}

export const useDataSubjectsQuery = createApiQueryHook(
  ['dataSubjects'],
  getDataSubjects,
);
